import { useRef, useState } from "react";
import {
  Download,
  ExternalLink,
  Eye,
  FileText,
  Image as ImageIcon,
  Lock,
  Loader2,
  Paperclip,
  Play,
  ShieldCheck,
  Upload,
  Video,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { uploadAttachment } from "@/api/attachments";
import { downloadAttachment, openAttachmentInNewTab } from "@/api/taskAttachments";
import { isPdf, isVideo } from "@/components/attachments/constants";
import { AttachmentViewer } from "@/components/attachments/AttachmentViewer";
import { complaintAttachmentDownloadUrl } from "@/api/complaints";
import { useUploadComplaintAttachment } from "@/hooks/useComplaints";
import type { ComplaintAttachment, ComplaintAttachmentType, ComplaintState } from "@/api/types";
import { ATTACHMENT_TYPE_LABEL, formatDateTime } from "./constants";

/** Per-type row icon (W4 emoji → Lucide: image / video / file-text). */
function rowIcon(mime: string) {
  const k = previewKind(mime);
  if (k === "image") return ImageIcon;
  if (k === "video") return Video;
  return FileText;
}

/**
 * 0-dead preview gating (§0). Only types with a real, safe in-app preview show
 * «Переглянути»/«Відкрити»; everything else is download-only.
 *  - image  (jpeg/png/webp) → AttachmentViewer modal (object-contain)
 *  - pdf                    → window.open(blob) → browser PDF viewer
 *  - video  (mp4/mov)       → AttachmentViewer inline <video>
 *  - other  (tiff/svg/heic/doc/docx) → download-only (browser can't safely render)
 */
const PREVIEWABLE_IMAGE = new Set(["image/jpeg", "image/png", "image/webp"]);
function previewKind(mime: string): "image" | "pdf" | "video" | "none" {
  const m = (mime || "").toLowerCase();
  if (PREVIEWABLE_IMAGE.has(m)) return "image";
  if (isPdf(m)) return "pdf";
  if (isVideo(m)) return "video";
  return "none";
}

const TYPE_OPTIONS: ComplaintAttachmentType[] = [
  "photo_evidence",
  "video_evidence",
  "customer_email",
  "document",
  "correspondence",
  "resolution_proof",
  "other_attachment",
];

export function AttachmentsPanel({
  complaintId,
  attachments,
  canUpload,
  canDownload = true,
  evidenceLocked = false,
  state,
}: {
  complaintId: string;
  attachments: ComplaintAttachment[];
  canUpload: boolean;
  // COMP-UX W5 — permission-aware evidence controls.
  canDownload?: boolean;
  evidenceLocked?: boolean;
  state?: ComplaintState;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [type, setType] = useState<ComplaintAttachmentType>("photo_evidence");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [viewer, setViewer] = useState<
    { url: string; filename: string; mime: string } | null
  >(null);
  const uploadMut = useUploadComplaintAttachment(complaintId);

  // PDF opens in the browser's own top-level viewer (no safe in-app sandbox for an
  // auth-blob); fetches authed bytes → object URL → window.open.
  async function onOpenTab(att: ComplaintAttachment) {
    setBusyId(att.attachmentId);
    setDownloadError(null);
    try {
      await openAttachmentInNewTab(
        complaintAttachmentDownloadUrl(complaintId, att.attachmentId),
      );
    } catch (e) {
      setDownloadError((e as Error)?.message ?? "Не вдалося відкрити файл");
    } finally {
      setBusyId(null);
    }
  }

  // Auth on this deploy is the Espo-Authorization token HEADER (no session cookie);
  // a bare <a href> 401s (and stock Espo answers a non-XHR GET with
  // WWW-Authenticate: Basic → native login popup). Fetch authed bytes → object URL.
  async function onDownload(att: ComplaintAttachment) {
    setDownloadingId(att.attachmentId);
    setDownloadError(null);
    try {
      await downloadAttachment(
        complaintAttachmentDownloadUrl(complaintId, att.attachmentId),
        att.fileName,
      );
    } catch (e) {
      setDownloadError((e as Error)?.message ?? "Не вдалося завантажити файл");
    } finally {
      setDownloadingId(null);
    }
  }

  async function onFile(file: File) {
    setBusy(true);
    setError(null);
    try {
      const att = await uploadAttachment(file, "CClientComplaintAttachment", "file");
      await uploadMut.mutateAsync({ fileId: att.id, attachmentType: type });
    } catch (e) {
      setError((e as Error)?.message ?? "Не вдалося завантажити файл");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  return (
    <div className="space-y-3">
      {attachments.length === 0 ? (
        <p className="text-sm text-muted-foreground">Вкладень ще немає.</p>
      ) : (
        <ul className="space-y-2">
          {attachments.map((a) => {
            const locked = a.lockedAsAppliedEvidence || a.lockedAsProposedEvidence;
            const RowIcon = rowIcon(a.fileMimeType);
            return (
              <li
                key={a.attachmentId}
                className="flex items-center gap-3 rounded-md border bg-background px-3 py-2"
              >
                <RowIcon className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="truncate text-sm font-medium">{a.fileName}</span>
                    <span className="rounded-full border border-border bg-muted px-1.5 py-0.5 text-[11px] text-muted-foreground">
                      {ATTACHMENT_TYPE_LABEL[a.attachmentType]}
                    </span>
                    {locked && (
                      <span className="inline-flex items-center gap-1 text-[11px] text-success-foreground">
                        <Lock className="h-3 w-3" aria-hidden />
                        {a.lockedAsAppliedEvidence ? "доказ (застосовано)" : "доказ (заблоковано)"}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-x-2 text-[12px] text-muted-foreground">
                    {a.uploadedBy?.name && <span>{a.uploadedBy.name}</span>}
                    {a.uploadedAt && <span>· {formatDateTime(a.uploadedAt)}</span>}
                    {a.sha256 && (
                      <span className="inline-flex items-center gap-1" title={a.sha256}>
                        <ShieldCheck className="h-3 w-3" aria-hidden /> SHA-256 {a.sha256.slice(0, 10)}…
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-0.5">
                  {(() => {
                    const kind = previewKind(a.fileMimeType);
                    const url = complaintAttachmentDownloadUrl(complaintId, a.attachmentId);
                    if (kind === "image" || kind === "video") {
                      return (
                        <button
                          type="button"
                          onClick={() =>
                            setViewer({ url, filename: a.fileName, mime: a.fileMimeType })
                          }
                          className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
                          title="Переглянути"
                          aria-label={`Переглянути ${a.fileName}`}
                        >
                          {kind === "video" ? (
                            <Play className="h-4 w-4" aria-hidden />
                          ) : (
                            <Eye className="h-4 w-4" aria-hidden />
                          )}
                        </button>
                      );
                    }
                    if (kind === "pdf") {
                      return (
                        <button
                          type="button"
                          disabled={busyId === a.attachmentId}
                          onClick={() => void onOpenTab(a)}
                          className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
                          title="Відкрити в новій вкладці"
                          aria-label={`Відкрити ${a.fileName} в новій вкладці`}
                        >
                          {busyId === a.attachmentId ? (
                            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                          ) : (
                            <ExternalLink className="h-4 w-4" aria-hidden />
                          )}
                        </button>
                      );
                    }
                    return null;
                  })()}
                  {canDownload && (
                    <button
                      type="button"
                      disabled={downloadingId === a.attachmentId}
                      onClick={() => void onDownload(a)}
                      className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
                      title="Завантажити"
                      aria-label={`Завантажити ${a.fileName}`}
                    >
                      {downloadingId === a.attachmentId ? (
                        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                      ) : (
                        <Download className="h-4 w-4" aria-hidden />
                      )}
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {downloadError && (
        <p role="alert" className="text-xs text-destructive">
          {downloadError}
        </p>
      )}

      {canUpload && (
        <div className="rounded-md border border-dashed bg-muted/20 p-3">
          <div className="flex flex-wrap items-center gap-2">
            <Paperclip className="h-4 w-4 text-muted-foreground" aria-hidden />
            <select
              value={type}
              onChange={(e) => setType(e.target.value as ComplaintAttachmentType)}
              className="h-9 rounded-md border bg-background px-2 text-sm"
            >
              {TYPE_OPTIONS.map((t) => (
                <option key={t} value={t}>
                  {ATTACHMENT_TYPE_LABEL[t]}
                </option>
              ))}
            </select>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="gap-1.5"
              disabled={busy}
              onClick={() => fileRef.current?.click()}
            >
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
              Завантажити файл
            </Button>
            <input
              ref={fileRef}
              type="file"
              className="hidden"
              accept=".pdf,.jpg,.jpeg,.png,.tiff,.doc,.docx,.mp4,.mov,video/mp4,video/quicktime"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void onFile(f);
              }}
            />
          </div>
          <p className="mt-1.5 text-[12px] text-muted-foreground">
            PDF, JPEG, PNG, TIFF, DOC, DOCX — до 20 МБ; відео MP4/MOV — до 60 МБ. Докази фінансового рішення блокуються після пропозиції.
          </p>
          {error && (
            <p role="alert" className="mt-1 text-xs text-destructive">
              {error}
            </p>
          )}
        </div>
      )}

      {/* W5 — explain why upload is unavailable (read-only / evidence-locked). */}
      {!canUpload && (evidenceLocked || state === "closed" || state === "rejected") && (
        <div className="flex items-center gap-2 rounded-md border bg-muted/30 px-3 py-2 text-[12px] text-muted-foreground">
          <Lock className="h-3.5 w-3.5 shrink-0" aria-hidden />
          {evidenceLocked
            ? "Докази заблоковано після пропозиції рішення — завантаження недоступне."
            : "Перегляд лише для читання — завантаження недоступне."}
        </div>
      )}

      {viewer && (
        <AttachmentViewer
          downloadUrl={viewer.url}
          filename={viewer.filename}
          mime={viewer.mime}
          onClose={() => setViewer(null)}
        />
      )}
    </div>
  );
}
